import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;

export default function CTASection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: false,
    margin: '-80px'
  });
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center center'] });
  const scaleTransform = useTransform(scrollYProgress, [0, 1], [0.94, 1]);
  const ctaScale = reduceMotion ? 1 : scaleTransform;

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger
      }
    }
  };

  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 8
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: duration * 0.6,
        ease: appleEase
      }
    }
  };

  const slideUp = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration,
        ease: appleEase
      }
    }
  };

  const lineReveal = {
    hidden: {
      scaleX: 0,
      originX: 0.5
    },
    visible: {
      scaleX: 1,
      transition: {
        duration: duration * 0.7,
        ease: appleEase
      }
    }
  };

  return (
    <section ref={ref} className="pt-24 md:pt-32 lg:pt-40 pb-8 md:pb-12 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          style={{ scale: ctaScale }}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.p variants={fadeIn} className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
            Start today
          </motion.p>

          <motion.h2 variants={slideUp} className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-4">
            Ready to unlock your potential?
          </motion.h2>

          <motion.div variants={lineReveal} className="h-[2px] w-20 bg-gradient-to-r from-transparent via-foreground/30 to-transparent mx-auto mb-8" />

          <motion.p variants={slideUp} className="text-lg md:text-xl text-muted-foreground leading-[1.6] mb-10 max-w-xl mx-auto">
            Take the first step. Learn at your own pace and achieve your own goals—not someone else's.
          </motion.p>

          <motion.div variants={slideUp}>
            <Link to="/role-select">
              <Button size="lg" className="group bg-accent text-accent-foreground hover:bg-[hsl(44,100%,45%)] text-base px-10 py-6 rounded-full font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-accent/30 hover:scale-[1.02] active:scale-[0.98]">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>

          <motion.p variants={fadeIn} className="mt-6 text-sm text-muted-foreground">
            Built in Launceston for TCE students across Tasmania.
          </motion.p>
        </motion.div>
      </div>
    </section>);

}